import { unzipSync } from "fflate"

/**
 * Browser-side parser for the global STAR product master `PRODUCT (*).xlsx`.
 *
 * Resolves both "PRODUCT (ACTIVE)" and "PRODUCT (INACTIVE)" sheets by name,
 * finds the "Product Code" (col B) letter from each header row, and counts the
 * data rows that carry a non-blank product code. Cells are inline strings
 * (sharedStrings.xml is present but empty), so the shared-string load is a
 * harmless fallback.
 */
export async function parseStarMasterProductForPreview(
  file: File,
): Promise<{
  activeCount: number
  inactiveCount: number
  total: number
}> {
  const buf = await file.arrayBuffer()
  const data = new Uint8Array(buf)
  const decode = (b: Uint8Array) => new TextDecoder().decode(b)

  // ── 1. Resolve both sheet paths by name ──────────────────────────────────
  const metaFiles = unzipSync(data, {
    filter: (f) =>
      f.name === "xl/workbook.xml" || f.name === "xl/_rels/workbook.xml.rels",
  })
  const wbBytes = metaFiles["xl/workbook.xml"]
  const relsBytes = metaFiles["xl/_rels/workbook.xml.rels"]
  if (!wbBytes || !relsBytes) throw new Error("workbook metadata missing")

  const wbXml = decode(wbBytes)
  const relsXml = decode(relsBytes)

  const activePath = resolveSheetPath(wbXml, relsXml, "PRODUCT (ACTIVE)")
  const inactivePath = resolveSheetPath(wbXml, relsXml, "PRODUCT (INACTIVE)")

  // ── 2. Load shared strings (may be empty for inline-string files) ─────────
  const wantedPaths = new Set([activePath, inactivePath])
  const extracted = unzipSync(data, {
    filter: (f) => f.name === "xl/sharedStrings.xml" || wantedPaths.has(f.name),
  })

  const sharedStrings: string[] = []
  const ssBytes = extracted["xl/sharedStrings.xml"]
  if (ssBytes) {
    const ssXml = decode(ssBytes)
    const parts = ssXml.split("<si>")
    for (let i = 1; i < parts.length; i++) {
      const matches = [...parts[i].matchAll(/<t(?:[^>]*)>(.*?)<\/t>/gs)]
      sharedStrings.push(matches.map((m) => m[1]).join(""))
    }
  }

  // ── 3. Count product rows per sheet ───────────────────────────────────────
  const activeCount = countProductRows(extracted[activePath], decode, sharedStrings)
  const inactiveCount = countProductRows(extracted[inactivePath], decode, sharedStrings)

  return { activeCount, inactiveCount, total: activeCount + inactiveCount }
}

function countProductRows(
  wsBytes: Uint8Array | undefined,
  decode: (b: Uint8Array) => string,
  sharedStrings: string[],
): number {
  if (!wsBytes) throw new Error("PRODUCT sheet not found in file")
  const wsXml = decode(wsBytes)
  const rowParts = wsXml.split("<row ")
  if (rowParts.length < 2) return 0

  // Map the "Product Code" column letter from the header row.
  let codeCol = ""
  const headerEnd = rowParts[1].indexOf("</row>")
  const headerXml = rowParts[1].slice(0, headerEnd >= 0 ? headerEnd : undefined)
  for (const cellXml of headerXml.split("<c ").slice(1)) {
    const letterMatch = cellXml.match(/r="([A-Z]+)\d+"/)
    if (!letterMatch) continue
    const colName = xlsxCellString(cellXml, sharedStrings)
    if (colName && colName.trim() === "Product Code") {
      codeCol = letterMatch[1]
      break
    }
  }
  if (!codeCol) throw new Error("Column 'Product Code' not found in sheet")

  // Count data rows whose Product Code cell is non-blank.
  let count = 0
  for (let i = 2; i < rowParts.length; i++) {
    const end = rowParts[i].indexOf("</row>")
    const content = end >= 0 ? rowParts[i].slice(0, end) : rowParts[i]
    if (!content.includes("<v>") && !content.includes("<is>")) continue
    for (const cellXml of content.split("<c ").slice(1)) {
      const letterMatch = cellXml.match(/r="([A-Z]+)\d+"/)
      if (!letterMatch || letterMatch[1] !== codeCol) continue
      const val = xlsxCellValue(cellXml, sharedStrings)
      if (val != null && val.trim() !== "") count++
      break
    }
  }
  return count
}

// ── Sheet path resolution ──────────────────────────────────────────────────────

function resolveSheetPath(wbXml: string, relsXml: string, sheetName: string): string {
  const rId = extractSheetRIdByName(wbXml, sheetName)
  if (!rId) throw new Error(`Sheet '${sheetName}' not found in workbook`)
  const target = extractRelTarget(relsXml, rId)
  if (!target) throw new Error(`Relationship target missing for '${sheetName}' sheet`)

  if (target.startsWith("../")) return target.slice(3)
  if (target.startsWith("/")) return target.slice(1)
  return `xl/${target}`
}

function extractSheetRIdByName(wbXml: string, sheetName: string): string | null {
  const escaped = sheetName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  const re = new RegExp(`<sheet\\b[^>]*name="${escaped}"[^>]*>`)
  const match = wbXml.match(re)
  if (!match) return null
  const m = match[0].match(/\br:id="([^"]+)"/)
  return m ? m[1] : null
}

function extractRelTarget(relsXml: string, rId: string): string | null {
  const parts = relsXml.split(/<Relationship\b/)
  for (let i = 1; i < parts.length; i++) {
    if (parts[i].includes(`Id="${rId}"`)) {
      const m = parts[i].match(/\bTarget="([^"]+)"/)
      if (m) return m[1]
    }
  }
  return null
}

// ── XLSX cell helpers ──────────────────────────────────────────────────────────

function xlsxCellString(cellXml: string, ss: string[]): string | null {
  if (cellXml.includes('t="s"')) {
    const m = cellXml.match(/<v>(\d+)<\/v>/)
    return m ? (ss[parseInt(m[1], 10)] ?? null) : null
  }
  if (cellXml.includes('t="inlineStr"')) {
    const matches = [...cellXml.matchAll(/<t(?:[^>]*)>(.*?)<\/t>/gs)]
    return matches.length > 0 ? matches.map((m) => m[1]).join("") : null
  }
  if (cellXml.includes('t="str"')) {
    const m = cellXml.match(/<v>([^<]*)<\/v>/)
    return m ? m[1] : null
  }
  return null
}

function xlsxCellValue(cellXml: string, ss: string[]): string | null {
  if (cellXml.includes('t="inlineStr"')) {
    const matches = [...cellXml.matchAll(/<t(?:[^>]*)>(.*?)<\/t>/gs)]
    return matches.length > 0 ? matches.map((m) => m[1]).join("") : null
  }
  if (cellXml.includes('t="s"')) {
    const m = cellXml.match(/<v>(\d+)<\/v>/)
    return m ? (ss[parseInt(m[1], 10)] ?? null) : null
  }
  if (cellXml.includes('t="str"')) {
    const m = cellXml.match(/<v>([^<]*)<\/v>/)
    return m ? m[1] : null
  }
  const m = cellXml.match(/<v>([^<]*)<\/v>/)
  return m ? m[1] : null
}
